import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const nowLocalDate = () => new Date().toISOString().slice(0, 10);

/**
 * Reusable "Receive Payment" dialog.
 * Props:
 *   open, onOpenChange, onSaved(payment)
 *   defaultCustomerId, defaultSaleId, defaultInvoiceNo, defaultAmount
 */
export default function ReceivePaymentDialog({
    open, onOpenChange, onSaved,
    defaultCustomerId = "", defaultSaleId = "", defaultInvoiceNo = "", defaultAmount = 0,
}) {
    const [customers, setCustomers] = useState([]);
    const [sales, setSales] = useState([]);
    const [form, setForm] = useState({
        customer_id: defaultCustomerId,
        sale_id: defaultSaleId,
        invoice_no: defaultInvoiceNo,
        payment_date: nowLocalDate(),
        payment_mode: "cash",
        amount: defaultAmount || "",
        reference_no: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [c, s] = await Promise.all([
                    api.get("/customers"),
                    api.get("/sales", { params: { limit: 500 } }),
                ]);
                setCustomers(c.data);
                setSales(s.data.filter((x) => x.status === "completed"));
            } catch (e) {
                toast.error(apiErrorMessage(e));
            }
        })();
        setForm((f) => ({
            ...f,
            customer_id: defaultCustomerId || "",
            sale_id: defaultSaleId || "",
            invoice_no: defaultInvoiceNo || "",
            amount: defaultAmount || "",
            payment_date: nowLocalDate(),
        }));
    }, [open, defaultCustomerId, defaultSaleId, defaultInvoiceNo, defaultAmount]);

    const filteredSales = useMemo(
        () => (form.customer_id ? sales.filter((s) => s.customer_id === form.customer_id) : sales),
        [sales, form.customer_id]
    );

    const selectedSale = sales.find((s) => s.id === form.sale_id);
    const suggested = selectedSale ? selectedSale.total : 0;

    const submit = async () => {
        const amt = Number(form.amount || 0);
        if (amt <= 0) return toast.error("Enter a valid amount");
        setSaving(true);
        try {
            const payload = {
                customer_id: form.customer_id || null,
                sale_id: form.sale_id || null,
                invoice_no: form.invoice_no || (selectedSale ? selectedSale.order_no : ""),
                payment_date: form.payment_date ? new Date(form.payment_date).toISOString() : null,
                payment_mode: form.payment_mode,
                amount: amt,
                reference_no: form.reference_no,
                notes: form.notes,
            };
            const { data } = await api.post("/payments", payload);
            toast.success(`Receipt ${data.receipt_no} created`);
            onOpenChange(false);
            onSaved && onSaved(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold font-display">Receive Payment</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Customer</Label>
                        <Select
                            value={form.customer_id || "walkin"}
                            onValueChange={(v) => setForm({ ...form, customer_id: v === "walkin" ? "" : v, sale_id: "", invoice_no: "" })}
                        >
                            <SelectTrigger className="mt-1.5" data-testid="payment-form-customer"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="walkin">Walk-in Customer</SelectItem>
                                {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Against Invoice</Label>
                        <Select
                            value={form.sale_id || "none"}
                            onValueChange={(v) => {
                                if (v === "none") {
                                    setForm({ ...form, sale_id: "", invoice_no: "" });
                                } else {
                                    const s = sales.find((x) => x.id === v);
                                    setForm({
                                        ...form, sale_id: v,
                                        invoice_no: s ? s.order_no : "",
                                        customer_id: form.customer_id || (s ? s.customer_id : "") || "",
                                        amount: form.amount || (s ? s.total : ""),
                                    });
                                }
                            }}
                        >
                            <SelectTrigger className="mt-1.5" data-testid="payment-form-invoice"><SelectValue placeholder="On-account (no specific invoice)" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— On-account —</SelectItem>
                                {filteredSales.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.order_no} · {formatCurrency(s.total)} · {s.customer_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Payment Date</Label>
                        <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" data-testid="payment-form-date" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Payment Mode</Label>
                        <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                            <SelectTrigger className="mt-1.5" data-testid="payment-form-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                <SelectItem value="cheque">Cheque</SelectItem>
                                <SelectItem value="cod">Cash on Delivery (COD)</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
                            Amount Received {suggested > 0 && <span className="text-neutral-400 normal-case tracking-normal">(invoice: {formatCurrency(suggested)})</span>}
                        </Label>
                        <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="payment-form-amount" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Reference No. / Txn ID</Label>
                        <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="mt-1.5" placeholder="UTR / Cheque no / UPI ref" data-testid="payment-form-reference" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" data-testid="payment-form-notes" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-form-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="payment-form-submit">
                        {saving ? "Saving..." : "Save & Generate Receipt"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
